/**
 * 画布：375 × page.height 设计尺寸，transform: scale(zoom)。
 * 交互：点击选中 / shift 多选 / 拖拽移动（吸附）/ 缩放手柄 / 旋转手柄 / 物料拖入。
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as RPointerEvent } from 'react';
import { CANVAS_HEIGHT, CANVAS_WIDTH, type Background, type Layer, type Rect } from '@h5/schema';
import { getMaterial } from '@h5/materials';
import { selectCurrentPage, useEditorStore } from '../store/store';
import { LayerView } from './LayerView';
import { LayerFrame, type HandleDir } from './LayerFrame';
import { computeSnap } from './snap';
import { designViewport, layerToStyle } from './layout';

export const MATERIAL_DND_TYPE = 'application/x-h5-material';

type DragState =
  | { kind: 'move'; ids: string[]; startX: number; startY: number; origin: Record<string, Rect> }
  | { kind: 'resize'; id: string; dir: HandleDir; startX: number; startY: number; origin: Rect; keepRatio: boolean }
  | { kind: 'rotate'; id: string; cx: number; cy: number; startAngle: number; originRotate: number };

function backgroundStyle(bg: Background): CSSProperties {
  if (bg.type === 'color') return { background: bg.color };
  return {
    backgroundColor: bg.color ?? '#fff',
    backgroundImage: bg.src ? `url(${bg.src})` : undefined,
    backgroundSize: bg.fit === 'fill' ? '100% 100%' : bg.fit,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
}

export function Canvas() {
  const page = useEditorStore(selectCurrentPage);
  const zoom = useEditorStore((s) => s.ui.zoom);
  const selectedIds = useEditorStore((s) => s.ui.selectedIds);
  const { select, clearSelection, moveLayer, moveLayers, beginBatch, endBatch, addLayer } = useEditorStore.getState();

  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [snapLines, setSnapLines] = useState<{ v: number[]; h: number[] }>({ v: [], h: [] });

  const pageHeight = page ? (page.mode === 'long' ? page.height : CANVAS_HEIGHT) : CANVAS_HEIGHT;
  const viewport = useMemo(() => designViewport(page?.mode ?? 'fixed', pageHeight), [page?.mode, pageHeight]);

  /** 屏幕坐标 → 设计坐标 */
  const toDesign = useCallback((clientX: number, clientY: number) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: (clientX - rect.left) / zoom, y: (clientY - rect.top) / zoom };
  }, [zoom]);

  // ---------- 指针交互 ----------
  const onLayerPointerDown = (e: RPointerEvent, layer: Layer) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    let ids: string[];
    if (e.shiftKey) {
      select([layer.id], { toggle: true });
      ids = useEditorStore.getState().ui.selectedIds;
    } else if (selectedIds.includes(layer.id)) {
      ids = selectedIds;
    } else {
      select([layer.id]);
      ids = [layer.id];
    }
    if (layer.locked) return;
    const pg = useEditorStore.getState().doc;
    const origin: Record<string, Rect> = {};
    for (const p of [...pg.pages, ...pg.popups]) for (const l of p.layers) if (ids.includes(l.id) && !l.locked) origin[l.id] = { ...l.rect };
    if (Object.keys(origin).length === 0) return;
    dragRef.current = { kind: 'move', ids: Object.keys(origin), startX: e.clientX, startY: e.clientY, origin };
    beginBatch();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onResizeStart = (layer: Layer, dir: HandleDir, e: RPointerEvent) => {
    const def = getMaterial(layer.type);
    dragRef.current = {
      kind: 'resize', id: layer.id, dir, startX: e.clientX, startY: e.clientY,
      origin: { ...layer.rect }, keepRatio: !!def?.capabilities.keepRatio || e.shiftKey,
    };
    beginBatch();
  };

  const onRotateStart = (layer: Layer, e: RPointerEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const cx = rect.left + (layer.rect.x + layer.rect.w / 2) * zoom;
    const cy = rect.top + (layer.rect.y + layer.rect.h / 2) * zoom;
    const startAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
    dragRef.current = { kind: 'rotate', id: layer.id, cx, cy, startAngle, originRotate: layer.rect.rotate };
    beginBatch();
  };

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || !page) return;
      if (d.kind === 'move') {
        let dx = (e.clientX - d.startX) / zoom;
        let dy = (e.clientY - d.startY) / zoom;
        // 吸附：以第一个图层为准
        const firstId = d.ids[0]!;
        const first = d.origin[firstId]!;
        const others = page.layers.filter((l) => !d.ids.includes(l.id));
        const snap = e.altKey ? { dx: 0, dy: 0, vLines: [], hLines: [] } : computeSnap({ ...first, x: first.x + dx, y: first.y + dy }, others, pageHeight);
        dx += snap.dx; dy += snap.dy;
        setSnapLines({ v: snap.vLines, h: snap.hLines });
        for (const id of d.ids) {
          const o = d.origin[id]!;
          moveLayer(id, { x: Math.round(o.x + dx), y: Math.round(o.y + dy) });
        }
      } else if (d.kind === 'resize') {
        const dx = (e.clientX - d.startX) / zoom;
        const dy = (e.clientY - d.startY) / zoom;
        const o = d.origin;
        let { x, y, w, h } = o;
        if (d.dir.includes('e')) w = o.w + dx;
        if (d.dir.includes('s')) h = o.h + dy;
        if (d.dir.includes('w')) { w = o.w - dx; x = o.x + dx; }
        if (d.dir.includes('n')) { h = o.h - dy; y = o.y + dy; }
        if (d.keepRatio && o.w > 0 && o.h > 0) {
          const ratio = o.w / o.h;
          if (d.dir === 'n' || d.dir === 's') w = h * ratio;
          else if (d.dir === 'e' || d.dir === 'w') h = w / ratio;
          else { // 角点：以宽为准
            h = w / ratio;
            if (d.dir.includes('n')) y = o.y + o.h - h;
          }
          if (d.dir.includes('w') && (d.dir === 'n' || d.dir === 's')) x = o.x + (o.w - w) / 2;
        }
        w = Math.max(4, w); h = Math.max(4, h);
        moveLayer(d.id, { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) });
      } else if (d.kind === 'rotate') {
        const angle = Math.atan2(e.clientY - d.cy, e.clientX - d.cx) * 180 / Math.PI;
        let rotate = d.originRotate + (angle - d.startAngle);
        if (e.shiftKey) rotate = Math.round(rotate / 15) * 15;
        rotate = ((Math.round(rotate) % 360) + 360) % 360;
        moveLayer(d.id, { rotate });
      }
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      setSnapLines({ v: [], h: [] });
      endBatch();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [page, zoom, pageHeight, moveLayer, endBatch]);

  // ---------- 物料拖入 ----------
  const onDrop = (e: React.DragEvent) => {
    const type = e.dataTransfer.getData(MATERIAL_DND_TYPE);
    if (!type) return;
    e.preventDefault();
    const def = getMaterial(type);
    if (!def) return;
    const { x, y } = toDesign(e.clientX, e.clientY);
    addLayer(type, {
      name: def.name,
      materialVersion: def.version,
      rect: { x: Math.round(x - def.defaultSize.w / 2), y: Math.round(y - def.defaultSize.h / 2), w: def.defaultSize.w, h: def.defaultSize.h, rotate: 0 },
      props: { ...def.defaultProps },
    });
  };

  if (!page) return <div style={{ padding: 24 }}>没有页面</div>;

  const sortedLayers = [...page.layers].sort((a, b) => a.style.zIndex - b.style.zIndex);

  return (
    <div
      ref={wrapRef}
      onPointerDown={() => clearSelection()}
      style={{ flex: 1, overflow: 'auto', background: '#e8e8e8', display: 'flex', justifyContent: 'center', padding: 40 }}
    >
      <div
        style={{ width: CANVAS_WIDTH * zoom, height: pageHeight * zoom, flexShrink: 0, position: 'relative' }}
        onDragOver={(e) => { if (e.dataTransfer.types.includes(MATERIAL_DND_TYPE)) e.preventDefault(); }}
        onDrop={onDrop}
      >
        <div
          ref={canvasRef}
          className="h5-canvas"
          style={{
            width: CANVAS_WIDTH, height: pageHeight, transform: `scale(${zoom})`, transformOrigin: '0 0',
            position: 'absolute', left: 0, top: 0, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,.15)',
            ...backgroundStyle(page.background),
          }}
        >
          {sortedLayers.map((layer) => {
            const selected = selectedIds.includes(layer.id);
            const def = getMaterial(layer.type);
            return (
              <div
                key={layer.id}
                data-layer-id={layer.id}
                onPointerDown={(e) => onLayerPointerDown(e, layer)}
                style={{ ...layerToStyle(layer, viewport), cursor: layer.locked ? 'not-allowed' : 'move', userSelect: 'none' }}
              >
                <LayerView layer={layer} zoom={zoom} />
                {selected && (
                  <LayerFrame
                    zoom={zoom}
                    locked={layer.locked}
                    resizable={def?.capabilities.resizable ?? true}
                    rotatable={def?.capabilities.rotatable ?? true}
                    handlesOnly={selectedIds.length > 1}
                    onResizeStart={(dir, e) => onResizeStart(layer, dir, e)}
                    onRotateStart={(e) => onRotateStart(layer, e)}
                  />
                )}
              </div>
            );
          })}
          {/* 吸附线 */}
          {snapLines.v.map((x) => <div key={`v${x}`} style={{ position: 'absolute', left: x, top: 0, bottom: 0, width: 1 / zoom, background: '#f759ab', pointerEvents: 'none', zIndex: 99999 }} />)}
          {snapLines.h.map((y) => <div key={`h${y}`} style={{ position: 'absolute', top: y, left: 0, right: 0, height: 1 / zoom, background: '#f759ab', pointerEvents: 'none', zIndex: 99999 }} />)}
          {/* fixed 页在 long 高度下的一屏参考线 */}
          {page.mode === 'long' && <div style={{ position: 'absolute', top: CANVAS_HEIGHT, left: 0, right: 0, borderTop: `${1 / zoom}px dashed rgba(0,0,0,.25)`, pointerEvents: 'none' }} />}
        </div>
      </div>
    </div>
  );
}
